import {
  MachinePreferencesLocalStorageKey,
  MachinePreferencesUrlParam,
} from 'features/machine-preferences/enum';
import { Mode } from 'features/multi-mode/enums';

export const CALL_CENTER_URL_ALIASES = [
  MachinePreferencesUrlParam.CALL_CENTER_CODE,
  'callCenter',
  'CallCenter',
] as const;

/** Max length for a call center code after trim/uppercase. */
const CALL_CENTER_CODE_MAX_LENGTH = 32;

export interface MachinePreferencesInput {
  machineId?: string | null;
  callCenterCode?: string | null;
  stationNumber?: string | null;
}

/**
 * Sanitize a call center code for storage, API paths, and UI.
 * - trims + uppercases
 * - allowlist: A-Z and 0-9 only
 * - rejects empty / oversized / otherwise invalid input as null
 */
export const normalizeCallCenterCode = (code?: string | null) => {
  if (code == null) {
    return null;
  }

  const normalized = code.trim().toUpperCase();

  if (
    !normalized ||
    normalized.length > CALL_CENTER_CODE_MAX_LENGTH ||
    !/^[A-Z0-9]+$/.test(normalized)
  ) {
    return null;
  }

  return normalized;
};

export type CallCenterCodeSource = 'url' | 'user.home';

export interface ResolvedCallCenterCode {
  code: string | null;
  source: CallCenterCodeSource;
}

/**
 * VRI always uses the home call center. VRS prefers a shortcut/machine
 * preference code, then falls back to home.
 */
export const resolveSessionCallCenterCode = ({
  mode,
  machineCallCenterCode,
  homeCallCenterCode,
}: {
  mode: Mode;
  machineCallCenterCode?: string | null;
  homeCallCenterCode?: string | null;
}): ResolvedCallCenterCode => {
  switch (mode) {
    case Mode.VRI:
      return {
        code: normalizeCallCenterCode(homeCallCenterCode),
        source: 'user.home',
      };
    case Mode.VRS: {
      const requestedCallCenterCode = normalizeCallCenterCode(
        machineCallCenterCode
      );

      if (requestedCallCenterCode) {
        return {
          code: requestedCallCenterCode,
          source: 'url',
        };
      }

      return {
        code: normalizeCallCenterCode(homeCallCenterCode),
        source: 'user.home',
      };
    }
    default: {
      const exhaustive: never = mode;
      return exhaustive;
    }
  }
};

/**
 * Resolve call center using Redux machine prefs when present, otherwise
 * (VRS only) fall back to localStorage so validation works before hydration.
 * VRI never reads shortcut or localStorage.
 */
export const resolveSessionCallCenterCodeWithStorageFallback = (
  mode: Mode,
  machineCallCenterCode?: string | null,
  homeCallCenterCode?: string | null
): ResolvedCallCenterCode => {
  switch (mode) {
    case Mode.VRI:
      return resolveSessionCallCenterCode({
        mode,
        homeCallCenterCode,
      });
    case Mode.VRS: {
      const fromRedux = normalizeCallCenterCode(machineCallCenterCode);
      const fromStorage = fromRedux
        ? null
        : getMachinePreferencesFromLocalStorage().callCenterCode;

      return resolveSessionCallCenterCode({
        mode,
        machineCallCenterCode: fromRedux ?? fromStorage,
        homeCallCenterCode,
      });
    }
    default: {
      const exhaustive: never = mode;
      return exhaustive;
    }
  }
};

const getCallCenterCodeFromQuery = (searchParams: URLSearchParams) => {
  for (const key of CALL_CENTER_URL_ALIASES) {
    const value = searchParams.get(key);
    if (value == null || value.trim() === '') {
      continue;
    }

    // Invalid/malicious values are dropped (null) rather than persisted.
    return normalizeCallCenterCode(value);
  }

  return null;
};

export const getMachinePreferencesFromUrl = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const machineId = searchParams.get(MachinePreferencesUrlParam.MACHINE_ID);
  const callCenterCode = getCallCenterCodeFromQuery(searchParams);
  const stationNumber = searchParams.get(
    MachinePreferencesUrlParam.STATION_NUMBER
  );

  return { machineId, callCenterCode, stationNumber };
};

export const setMachinePreferencesInLocalStorage = ({
  machineId,
  callCenterCode,
  stationNumber,
}: MachinePreferencesInput) => {
  if (machineId) {
    localStorage.setItem(
      MachinePreferencesLocalStorageKey.MACHINE_ID,
      machineId
    );
  }

  if (callCenterCode !== undefined) {
    const sanitizedCallCenterCode = normalizeCallCenterCode(callCenterCode);

    if (sanitizedCallCenterCode) {
      localStorage.setItem(
        MachinePreferencesLocalStorageKey.CALL_CENTER_CODE,
        sanitizedCallCenterCode
      );
    } else {
      localStorage.removeItem(
        MachinePreferencesLocalStorageKey.CALL_CENTER_CODE
      );
    }
  }

  if (stationNumber) {
    localStorage.setItem(
      MachinePreferencesLocalStorageKey.STATION_NUMBER,
      stationNumber
    );
  }
};

export const getMachinePreferencesFromLocalStorage = () => {
  const machineId = localStorage.getItem(
    MachinePreferencesLocalStorageKey.MACHINE_ID
  );
  const callCenterCode = normalizeCallCenterCode(
    localStorage.getItem(MachinePreferencesLocalStorageKey.CALL_CENTER_CODE)
  );
  const stationNumber = localStorage.getItem(
    MachinePreferencesLocalStorageKey.STATION_NUMBER
  );

  return {
    machineId,
    callCenterCode,
    stationNumber,
  };
};
